import { yup } from '@framework/validation/initYup.ts';
import { RichTextAdvancedSchema } from '../../../../../inputs/rich-text-advanced/schema/RichTextAdvancedSchema.ts';
import { isExpressionReferenceOfObjectsArray } from '../../../../expressions/datatype/isExpressionReferenceOfObjectsArray.ts';
import { DefaultAssignableExpressionSchema } from '../../../../expressions/repository/assignable/AssignableExpressionSchema.ts';
import { ReferenceSchema } from '../../../../expressions/validation/ReferenceSchema.ts';
import { RulesSchema } from '../../../../rules/repository/RulesSchema.ts';
import { CommonBaseWidgetSchema } from '../../../CommonBaseWidgetSchema.ts';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { uniqueWidgetCodeSchema } from '../../../validation/uniqueWidgetCodeSchema.ts';
import { ConfirmationConfigInput } from '../../advanced/confirm/shared/ConfirmationConfigInput.tsx';
import { ConfirmationConfigSchema } from '../../advanced/confirm/shared/ConfirmationConfigSchema.ts';
import { WidgetConfigurationSlotRender } from '../../common/config-slots/WidgetConfigurationSlotRender.tsx';
import { WidgetConfigurationSlots } from '../../common/config-slots/WidgetConfigurationSlots.tsx';
import {
  DefaultValueInput,
  useDefaultValueField,
} from '../../common/DefaultValueInput.tsx';
import { type IInputWidget } from '../IInputWidget.ts';
import { CodeLabelInputForm } from './CodeLabelInputForm.tsx';
import { CommonHelpTextsInputConfigSubCard } from './CommonHelpTextsInputConfigSubCard.tsx';
import { getWidgetProps } from './util/getWidgetProps.ts';

export const CommonInputWidgetConfigSchema: yup.ObjectSchema<IInputWidget> =
  CommonBaseWidgetSchema.shape({
    rules: RulesSchema,
    code: uniqueWidgetCodeSchema,
    label: RichTextAdvancedSchema.nonNullable().label('Etichetta'),
    placeholder: yup.string().optional().label('Placeholder'),
    helpText: RichTextAdvancedSchema.optional()
      .default([])
      .label('Testo di aiuto'),
    defaultValue: DefaultAssignableExpressionSchema.optional(),
    confirmation: ConfirmationConfigSchema,
    defaultValueChildren: yup
      .array(ReferenceSchema.label('Riferimento').nullable())
      .when(['defaultValue'], ([defaultValue], schema) =>
        isExpressionReferenceOfObjectsArray(defaultValue)
          ? schema.required()
          : schema.notRequired().strip(),
      ),
  }).required();

export interface ICommonConfigComponentProps
  extends IWidgetComponentProps<IInputWidget> {
  hideConfigPlaceholder?: boolean;
  hideConfigDefaultValue?: boolean;
  hideConfigHelpText?: boolean;
  hideConfigLabel?: boolean;
  /** Impedisci di impostare il collegamento a un widget di conferma */
  noConfirmation?: boolean;
}

/**
 * Preset del form di configurazione per i widget input standard: assembla i
 * blocchi condivisi (CodeLabelInputForm, CommonHelpTextsInputConfigSubCard,
 * fill Compilazione). I widget con esigenze di ordine particolari compongono
 * direttamente i singoli blocchi.
 */
export function CommonConfigComponent(props: ICommonConfigComponentProps) {
  const options = getWidgetProps({ ...props, preview: true });
  const defaultValueField = useDefaultValueField();

  return (
    <div>
      <CodeLabelInputForm
        widgetOptions={options}
        hideLabel={props.hideConfigLabel}
      />

      <CommonHelpTextsInputConfigSubCard
        hidePlaceholder={props.hideConfigPlaceholder}
        hideHelpText={props.hideConfigHelpText}
      />

      {/* La guardia sul field evita una card "Compilazione" vuota */}
      {!props.hideConfigDefaultValue && defaultValueField && (
        <WidgetConfigurationSlotRender
          slot={WidgetConfigurationSlots.Default}
          order={10}
        >
          <DefaultValueInput />
          {/* La conferma è impossibile senza un default */}
          {!props.noConfirmation && (
            <ConfirmationConfigInput name="confirmation" />
          )}
        </WidgetConfigurationSlotRender>
      )}
    </div>
  );
}
